import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { tokens } from "../theme";
import { errorText } from "../utils/apiError";
import { EditDrawer, type Dismiss } from "./EditDrawer";
import type { SignageAssignment } from "@shared/types/signage";
import {
  orientationOfResolution,
  rotationMismatch,
  type ScreenOrientation,
} from "@shared/media";

/**
 * The per-screen signage settings an operator can actually reach.
 *
 * ⛔ Every field here ALREADY worked end to end and had no control. `sound
 * Enabled` was honoured by the resolver, the legacy facade and the panel, and
 * the plan page only PRINTED it; `breaksPerHour` defaults to 0, which meant
 * campaign breaks could not be switched on by anybody, anywhere, at all. A
 * capability with no switch is not shipped, however well it is implemented.
 *
 * ## Orientation and panels belong to the UNIT (UNIT-2)
 *
 * How the screen is hung is a fact of the spot it hangs in, so a swapped
 * screen inherits it: this drawer edits the screen's UNIT orientation, on its
 * own audited route. The panel count moved to the Units editor, next to the
 * Vistar venue it multiplies.
 */

export interface SignageScreenSettingsDialogProps {
  open: boolean;
  screenLabel: string;
  assignment: SignageAssignment | undefined;
  /** The screen's unit. Absent = the screen is in no unit yet. */
  unit?: {
    id: string;
    orientation?: ScreenOrientation;
    reportedResolution?: string;
  };
  onClose: () => void;
  onSave: (patch: Partial<SignageAssignment>) => Promise<void>;
  /** Write the unit's orientation (`null` = not set). */
  onSaveOrientation?: (orientation: ScreenOrientation | null) => Promise<void>;
}

/**
 * The cadences upstream allows, plus off.
 *
 * Upstream restricts this to `0 | 10 | 20` and silently snaps anything else,
 * so a Foyer screen set to 4 would BEHAVE differently the day it is served
 * from the old portal - and a mixed site runs both. Offering the same three
 * keeps the two generations honest with each other; the resolver still accepts
 * any 0-60 for anything already stored.
 */
const CADENCES = [
  { value: 0, label: "common:labels.off" },
  // A break follows an ad block (every 6 minutes), so these are "every block"
  // and "every other block" (Sigge, 2026-10-20: 20 an hour was too much).
  { value: 5, label: "signage:signageScreenSettingsDialog.cadence.every12" },
  { value: 10, label: "signage:signageScreenSettingsDialog.cadence.every6" },
] as const;

export default function SignageScreenSettingsDialog({
  open,
  screenLabel,
  assignment,
  unit,
  onClose,
  onSave,
  onSaveOrientation,
}: SignageScreenSettingsDialogProps) {
  const { t } = useTranslation(["signage", "common"]);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [breaksPerHour, setBreaksPerHour] = useState(0);
  const [orientation, setOrientation] = useState<"" | ScreenOrientation>("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSoundEnabled(assignment?.soundEnabled ?? false);
    // A stored value outside the three offered is kept rather than snapped to
    // the nearest: silently changing a cadence somebody set is worse than
    // showing it.
    setBreaksPerHour(assignment?.breaksPerHour ?? 0);
    setOrientation(unit?.orientation ?? "");
    setError(null);
  }, [open, assignment, unit?.orientation]);

  const reported = orientationOfResolution(unit?.reportedResolution);
  const mismatch = rotationMismatch(
    orientation || null,
    unit?.reportedResolution
  );

  const known = CADENCES.some((c) => c.value === breaksPerHour);
  const orientationWord = (o: ScreenOrientation) =>
    t(`signageScreenSettingsDialog.orientationInline.${o}`);

  /** On success the drawer slides out first, then calls `onClose`. */
  const save = async (dismiss: Dismiss) => {
    setSaving(true);
    setError(null);
    try {
      if (
        unit &&
        onSaveOrientation &&
        (orientation || null) !== (unit.orientation ?? null)
      ) {
        await onSaveOrientation(orientation || null);
      }
      await onSave({ soundEnabled, breaksPerHour });
      dismiss();
    } catch (e) {
      setError(errorText(e, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditDrawer
      open={open}
      onClose={onClose}
      title={t("signageScreenSettingsDialog.title")}
      subtitle={screenLabel}
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()} disabled={saving}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void save(dismiss)}
            disabled={saving}
          >
            {saving ? t("common:states.saving") : t("common:buttons.save")}
          </Button>
        </>
      )}
    >
      <Stack gap={3}>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={soundEnabled}
                onChange={(e) => setSoundEnabled(e.target.checked)}
                inputProps={{ "aria-label": t("common:labels.sound") }}
              />
            }
            label={t("common:labels.sound")}
          />
          <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
            {soundEnabled
              ? t("signageScreenSettingsDialog.soundOnHelp")
              : t("signageScreenSettingsDialog.soundOffHelp")}
          </Typography>
        </Box>

        <Box>
          <TextField
            select
            fullWidth
            size="small"
            label={t("signageScreenSettingsDialog.campaignBreaks")}
            value={known ? breaksPerHour : "other"}
            onChange={(e) => setBreaksPerHour(Number(e.target.value))}
            inputProps={{
              "aria-label": t("signageScreenSettingsDialog.campaignBreaks"),
            }}
          >
            {CADENCES.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {t(c.label)}
              </MenuItem>
            ))}
            {!known && (
              <MenuItem value="other" disabled>
                {breaksPerHour > 10
                  ? t("signageScreenSettingsDialog.cadence.perHourPlaysAs", {
                      count: breaksPerHour,
                    })
                  : t(
                      "signageScreenSettingsDialog.cadence.perHourSetElsewhere",
                      { count: breaksPerHour }
                    )}
              </MenuItem>
            )}
          </TextField>
          <Typography variant="meta" sx={{ color: tokens.inkMuted, mt: 0.75 }}>
            {t("signageScreenSettingsDialog.campaignBreaksHelp")}
          </Typography>
        </Box>

        <Box>
          <TextField
            select
            fullWidth
            size="small"
            label={t("common:labels.orientation")}
            value={orientation}
            disabled={!unit || !onSaveOrientation}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            onChange={(e) =>
              setOrientation(e.target.value as "" | ScreenOrientation)
            }
            inputProps={{ "aria-label": t("common:labels.orientation") }}
          >
            <MenuItem value="">
              {reported
                ? t("signageScreenSettingsDialog.orientationNotSetReported", {
                    orientation: orientationWord(reported),
                  })
                : t("signageScreenSettingsDialog.orientationNotSet")}
            </MenuItem>
            <MenuItem value="landscape">
              {t("common:labels.landscape")}
            </MenuItem>
            <MenuItem value="portrait">{t("common:labels.portrait")}</MenuItem>
          </TextField>
          <Typography variant="meta" sx={{ color: tokens.inkMuted, mt: 0.75 }}>
            {unit
              ? t("signageScreenSettingsDialog.unitHung", { unit: unit.id })
              : t("signageScreenSettingsDialog.connectToUnit")}
          </Typography>
          {mismatch && (
            <Alert
              severity="warning"
              sx={{ mt: 1 }}
              data-testid="screen-orientation-mismatch"
            >
              {t("signageScreenSettingsDialog.mismatch", {
                orientation: orientationWord(mismatch),
              })}
            </Alert>
          )}
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 2.5 }}>
          {error}
        </Alert>
      )}
    </EditDrawer>
  );
}
